/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Actions,
  Company,
  MultiSelectOption,
  ResourceMeta,
  Subjects,
  TaskAssignedResource,
  TaskResponse,
  TaskType,
  getTaskType,
} from '@dispatch/shared/types';
import { Section, SectionDivider, TabPanel } from '@dispatch/web/ui';
import { Box, Chip } from '@mui/material';
import { grey } from '@mui/material/colors';
import React, { useMemo } from 'react';
import {
  Control,
  FieldErrors,
  FieldValue,
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import {
  LocationInformation,
  ResourceList,
} from '@dispatch/web/features/tasks';
import { Ability } from '@casl/ability';
import { AssignedResourceSection } from './AssignmentInformationSection/AssignedResourceSection';
import { TaskSection } from './AssignmentInformationSection/TaskSection';
import { ClaimAttributesSection } from './AssignmentInformationSection/ClaimAttributesSection';
import { TimingSection } from './AssignmentInformationSection/TimingSection';
import { isEmpty } from 'lodash';
import {
  useCurrentUserAsResourceId,
  useCurrentUserResource,
} from '@dispatch/shared/hooks';

export const AssignmentInformation = function ({
  currentTab,
  errors,
  control,
  attributeOptions,
  inspectionOptions,
  resourceData,
  resources,
  taskData,
  setValue,
  getValues,
  perils,
  renderTaskLock,
  permissions,
  handleReassign,
  resourceMeta,
  rescheduleReasons,
  company,
}: {
  currentTab: number;
  errors: FieldErrors;
  control: Control<FieldValues>;
  attributeOptions?: { title: string; value: string }[];
  inspectionOptions?: { title: string; value: string }[];
  resourceData?: TaskAssignedResource | null;
  company?: Company;
  resources?: any;
  taskData?: TaskResponse;
  setValue?: UseFormSetValue<FieldValue<any>>;
  getValues?: UseFormGetValues<FieldValues>;
  renderTaskLock?: () => React.ReactNode;
  perils: MultiSelectOption[];
  permissions: Ability<[Actions, Subjects]> | undefined;
  handleReassign: ({
    resourceId,
    reasonCode,
    forceReassign,
  }: {
    resourceId: string;
    reasonCode: string;
    forceReassign: boolean;
  }) => void;
  resourceMeta?: ResourceMeta;
  rescheduleReasons?: MultiSelectOption[];
}) {
  const [assignedResourceData, setAssignedResourceData] =
    React.useState<TaskAssignedResource | null>();
  const [isAssigning, setIsAssigning] = React.useState<boolean>(false);
  const isRequiredResource = !isEmpty(taskData?.$msg?.requiredResource);
  const { value: currentUserResourceId } = useCurrentUserAsResourceId();
  const { isAdjuster } = useCurrentUserResource();

  React.useEffect(() => {
    // setAssignedResourceData(resourceData);

    if (isAssigning) {
      setIsAssigning(false);
    }
  }, [isAssigning, resourceData]);

  const hasUpsertPermissions = permissions?.can?.('upsert', 'complex-loss');

  const canEdit = useMemo(() => {
    // complex-loss adjuster can only edit if they are assigned to the task
    // if not an adjuster then they should be able to edit if they have upsert permissions
    if (isAdjuster) {
      return (
        hasUpsertPermissions &&
        taskData?.$msg?.assignedResource?.id === currentUserResourceId
      );
    } else return hasUpsertPermissions;
  }, [
    isAdjuster,
    hasUpsertPermissions,
    taskData?.$msg?.assignedResource?.id,
    currentUserResourceId,
  ]);

  const showResourceList = useMemo(() => {
    return (
      !taskData?.$msg?.lockedAt &&
      (taskData?.$msg?.assignmentHandling?.appointment ||
        taskData?.$msg?.taskType === TaskType.COMPLEX) &&
      canEdit
    );
  }, [
    canEdit,
    taskData?.$msg?.assignmentHandling?.appointment,
    taskData?.$msg?.lockedAt,
    taskData?.$msg?.taskType,
  ]);

  const renderResourceList = React.useMemo(() => {
    return showResourceList ? (
      <ResourceList
        control={control}
        data={resources || []}
        getValues={getValues}
        handleReassign={handleReassign}
        resourceMeta={resourceMeta}
      />
    ) : null;
  }, [
    showResourceList,
    control,
    resources,
    getValues,
    handleReassign,
    resourceMeta,
  ]);

  const renderTimeSection = React.useMemo(() => {
    return (
      ![TaskType.COMPLEX].includes(getTaskType(taskData?.$msg?.taskType)) && (
        <>
          <SectionDivider />
          <Section title="Timing">
            <TimingSection
              getValues={getValues}
              taskData={taskData}
              control={control}
              errors={errors}
              hasUpsertPermissions={hasUpsertPermissions}
              hasAppointment={!!taskData?.$msg?.assignmentHandling?.appointment}
              setValue={setValue}
              rescheduleReasons={rescheduleReasons}
            />
          </Section>
        </>
      )
    );
  }, [taskData?.$msg.taskType]);

  return (
    <TabPanel value={currentTab} index={0} data-testid="general-info-tab">
      <Box sx={{ backgroundColor: grey['A100'], padding: '24px !important' }}>
        <Section
          title="Assigned Resource"
          rightTopControl={renderTaskLock}
          badge={
            isRequiredResource ? (
              <Chip label="Required" color="warning" size="small" />
            ) : undefined
          }
        >
          <AssignedResourceSection
            assignedResourceData={assignedResourceData}
            resourceData={resourceData}
            isAssigning={isAssigning}
            company={company}
          />
        </Section>
        <br />
        {renderResourceList}
      </Box>
      <Box sx={{ padding: '24px !important' }}>
        <Section title={`Task`}>
          <TaskSection
            control={control}
            errors={errors}
            hasUpsertPermissions={canEdit}
            inspectionOptions={inspectionOptions}
            perils={perils}
            taskData={taskData}
          />
        </Section>
        <SectionDivider />
        <Section title="Claim Attributes">
          <ClaimAttributesSection
            control={control}
            errors={errors}
            attributeOptions={attributeOptions}
          />
        </Section>
        <SectionDivider />
        <Section title="Location">
          <LocationInformation
            name="claim.locations"
            control={control}
            errors={errors}
          />
        </Section>
        <SectionDivider />
        {renderTimeSection}
      </Box>
    </TabPanel>
  );
};
